// Head Soccer 2D Client
import { sounds } from '../sound.js';

export class SoccerGameClient {
  constructor(socket, containerEl) {
    this.socket = socket;
    this.container = containerEl;
    this.players = {};
    this.ball = { x: 400, y: 300, r: 14 };
    this.scores = { p1: 0, p2: 0 };
    this.timer = 90;
    this.input = { left: false, right: false, jump: false, kick: false };

    this.render();
    this.setupSocketEvents();
    this.setupControls();
  }

  render() {
    this.container.innerHTML = `
      <div class="soccer-wrapper">
        <div class="soccer-header">
          <div class="soccer-scoreboard">
            <span class="score-p1" id="soccer-score-p1">🔴 0</span>
            <span class="timer-badge" id="soccer-timer">⏱️ 90s</span>
            <span class="score-p2" id="soccer-score-p2">0 🔵</span>
          </div>
          <div class="soccer-sub">A/D or Arrows to Move • W/Up to Jump • Space to Power Kick</div>
        </div>

        <div class="soccer-canvas-box">
          <canvas id="soccer-canvas" width="800" height="460"></canvas>
        </div>

        <!-- Virtual Touch D-Pad for Mobile Players -->
        <div class="mobile-touch-bar">
          <div class="touch-dpad-cluster">
            <button id="touch-soccer-left" class="btn-virtual-touch">◀</button>
            <button id="touch-soccer-right" class="btn-virtual-touch">▶</button>
          </div>
          <div class="touch-action-cluster">
            <button id="touch-soccer-jump" class="btn-virtual-touch">⬆️ JUMP</button>
            <button id="touch-soccer-kick" class="btn-virtual-touch btn-virtual-action">⚽ KICK</button>
          </div>
        </div>
      </div>
    `;

    this.canvas = document.getElementById('soccer-canvas');
    this.ctx = this.canvas.getContext('2d');
  }

  setupControls() {
    const handleKey = (e, isDown) => {
      let changed = false;
      const key = e.key ? e.key.toLowerCase() : '';
      if (e.code === 'KeyA' || key === 'a' || e.code === 'ArrowLeft') { this.input.left = isDown; changed = true; }
      if (e.code === 'KeyD' || key === 'd' || e.code === 'ArrowRight') { this.input.right = isDown; changed = true; }
      if (e.code === 'KeyW' || key === 'w' || e.code === 'ArrowUp') { this.input.jump = isDown; changed = true; }
      if (e.code === 'Space' || key === 'k') { this.input.kick = isDown; changed = true; }

      if (changed) {
        this.socket.emit('soccer_input', this.input);
      }
    };

    const attachTouchBtn = (btnId, keyName) => {
      const btn = document.getElementById(btnId);
      if (!btn) return;
      const setPressed = (pressed) => {
        if (this.input[keyName] !== pressed) {
          this.input[keyName] = pressed;
          this.socket.emit('soccer_input', this.input);
        }
      };
      btn.addEventListener('touchstart', (e) => { e.preventDefault(); setPressed(true); }, { passive: false });
      btn.addEventListener('touchend', (e) => { e.preventDefault(); setPressed(false); }, { passive: false });
      btn.addEventListener('mousedown', () => setPressed(true));
      btn.addEventListener('mouseup', () => setPressed(false));
      btn.addEventListener('mouseleave', () => setPressed(false));
    };

    attachTouchBtn('touch-soccer-left', 'left');
    attachTouchBtn('touch-soccer-right', 'right');
    attachTouchBtn('touch-soccer-jump', 'jump');
    attachTouchBtn('touch-soccer-kick', 'kick');

    this.keyDownHandler = (e) => handleKey(e, true);
    this.keyUpHandler = (e) => handleKey(e, false);

    window.addEventListener('keydown', this.keyDownHandler);
    window.addEventListener('keyup', this.keyUpHandler);
  }

  setupSocketEvents() {
    this.socket.on('soccer_started', (data) => {
      this.players = data.players;
      this.scores = data.scores;
      this.timer = data.timer;
      sounds.playChime();
      this.draw();
    });

    this.socket.on('soccer_state', (data) => {
      this.players = data.players;
      this.ball = data.ball;
      this.scores = data.scores;
      this.timer = data.timer;

      const p1Score = document.getElementById('soccer-score-p1');
      const p2Score = document.getElementById('soccer-score-p2');
      const timerBadge = document.getElementById('soccer-timer');

      if (p1Score) p1Score.innerText = `🔴 ${this.scores.p1}`;
      if (p2Score) p2Score.innerText = `${this.scores.p2} 🔵`;
      if (timerBadge) timerBadge.innerText = `⏱️ ${this.timer}s`;

      this.draw();
    });

    this.socket.on('soccer_goal', (data) => {
      sounds.playVictory();
      this.scores = data.scores;
    });

    this.socket.on('soccer_gameOver', (data) => {
      sounds.playVictory();
      alert(`🏆 MATCH FINISHED! Winner: ${data.winner?.name || 'Tie Game'} (${this.scores.p1} - ${this.scores.p2})`);
    });
  }

  draw() {
    if (!this.ctx) return;

    // Stadium Sky
    this.ctx.fillStyle = '#0f172a';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    // Green Turf Pitch
    this.ctx.fillStyle = '#15803d';
    this.ctx.fillRect(0, 400, this.canvas.width, 60);

    // Pitch White Lines
    this.ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
    this.ctx.lineWidth = 4;
    this.ctx.beginPath();
    this.ctx.moveTo(0, 400);
    this.ctx.lineTo(this.canvas.width, 400);
    this.ctx.stroke();

    // Center Circle & Line
    this.ctx.beginPath();
    this.ctx.moveTo(400, 400);
    this.ctx.lineTo(400, 260);
    this.ctx.stroke();
    this.ctx.beginPath();
    this.ctx.arc(400, 400, 60, Math.PI, 0);
    this.ctx.stroke();

    // Goalposts (Left & Right)
    this.ctx.strokeStyle = '#ffffff';
    this.ctx.lineWidth = 6;
    // Left Goal
    this.ctx.strokeRect(0, 280, 60, 120);
    // Right Goal
    this.ctx.strokeRect(740, 280, 60, 120);

    // Draw Players
    for (const id in this.players) {
      const p = this.players[id];

      // Big Head Avatar Circle
      this.ctx.fillStyle = p.color;
      this.ctx.shadowColor = p.color;
      this.ctx.shadowBlur = 10;
      this.ctx.beginPath();
      this.ctx.arc(p.x, p.y - 20, 22, 0, Math.PI * 2);
      this.ctx.fill();
      this.ctx.shadowBlur = 0;

      // Face Emojis
      this.ctx.font = '22px sans-serif';
      this.ctx.textAlign = 'center';
      this.ctx.fillText(p.team === 'p1' ? '😎' : '🤠', p.x, p.y - 12);

      // Kicking Boot / Body
      this.ctx.fillStyle = '#0f172a';
      this.ctx.fillRect(p.x - 10, p.y - 2, 20, 8);

      // Player Name
      this.ctx.fillStyle = '#ffffff';
      this.ctx.font = 'bold 11px Rajdhani, sans-serif';
      this.ctx.fillText(p.name, p.x, p.y - 48);
    }

    // Draw Bouncy Soccer Ball
    this.ctx.fillStyle = '#ffffff';
    this.ctx.shadowColor = '#ffffff';
    this.ctx.shadowBlur = 12;
    this.ctx.beginPath();
    this.ctx.arc(this.ball.x, this.ball.y, this.ball.r, 0, Math.PI * 2);
    this.ctx.fill();
    this.ctx.shadowBlur = 0;

    // Ball Hexagon Patterns
    this.ctx.fillStyle = '#0f172a';
    this.ctx.beginPath();
    this.ctx.arc(this.ball.x, this.ball.y, 5, 0, Math.PI * 2);
    this.ctx.fill();
  }

  destroy() {
    if (this.keyDownHandler) window.removeEventListener('keydown', this.keyDownHandler);
    if (this.keyUpHandler) window.removeEventListener('keyup', this.keyUpHandler);
    this.socket.off('soccer_started');
    this.socket.off('soccer_state');
    this.socket.off('soccer_goal');
    this.socket.off('soccer_gameOver');
  }
}
